import React, {Dispatch, Fragment, ReactElement, SetStateAction, useState} from "react";
import {ProcessErrorType, ProcessType} from "@process/types/process.types";
import {Button, Form} from "react-bootstrap";
import {ShipmentMethodSelect} from "@shipment/components";
import {ShipmentMethodKeyEnum, ShipmentMethodType, ShipmentMethodTypeEnum} from "@shipment/types/shipmentMethod.types";
import {AddressForm, FormError} from "@src/components";
import {DepartmentSelect} from "@department/components";
import {Maybe} from "@src/types/global.types";
import {DepartmentType} from "@department/types/department.types";
import {AddressType} from "@src/types/address.types";

interface ProcessFormIncomeShipmentProps {
    process: ProcessType
    setProcess: Dispatch<SetStateAction<ProcessType>>
    formErrors: ProcessErrorType
    removeFromFormErrors: Function
}

const ProcessFormIncomeShipment: React.FC<ProcessFormIncomeShipmentProps> = ({
    process, setProcess, formErrors, removeFromFormErrors
}): ReactElement => {

    const onIncomeShipmentMethodChangeHandler = (shipmentMethod: Maybe<ShipmentMethodType>) => {
        removeFromFormErrors('incomeShipment')
        setProcess((prevState:ProcessType) => ({
            ...prevState,
            hasDifferentIncomeShipmentDepartment: false,
            hasDifferentIncomeShipmentAddress: false,
            incomeShipment: {...prevState.incomeShipment, shipmentMethod}
        }));
    }

    const onIncomeShipmentDepartmentChangeHandler = (department: Maybe<DepartmentType>) => {
        setProcess(prevState => ({...prevState, incomeShipment: {...prevState.incomeShipment, department}}));
    }

    const setIncomeShipmentAddress = (address: Maybe<AddressType>) => {
        setProcess((prevState: ProcessType) => ({
            ...prevState, incomeShipment: {...prevState.incomeShipment, address}
        }));
    }

    const copyAddressFromCustomer = (e: React.MouseEvent<HTMLElement>) => {
        setProcess((prevState: ProcessType) => ({
            ...prevState, incomeShipment: {...prevState.incomeShipment, address: {...prevState.customer.address}}
        }));
    }

    const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        const name: string = e.target.name;
        const type: string = e.target.type;
        let value: string|boolean;

        if ('checkbox' === type) {
            value = e.target.checked;
        } else {
            value = e.target.value;
        }

        setProcess(prevState => ({...prevState, [name]: value}));
        removeFromFormErrors(name);
    }

    return (
        <>
            <Form.Group className="mb-3">
                <Form.Label>Metoda dostawy do serwisu</Form.Label>
                <ShipmentMethodSelect
                    type={ShipmentMethodTypeEnum.incoming}
                    value={process?.incomeShipment?.shipmentMethod}
                    onChange={onIncomeShipmentMethodChangeHandler}
                    className={formErrors?.incomeShipment?.shipmentMethod ? ' is-invalid' : ''}
                />
                <FormError errors={formErrors?.incomeShipment?.shipmentMethod} />
            </Form.Group>

            {process?.incomeShipment?.shipmentMethod?.key === ShipmentMethodKeyEnum.personal && (
                <>
                    <Form.Group className="mb-3" controlId="hasDifferentIncomeShipmentDepartment">
                        <Form.Check
                            type="switch"
                            name="hasDifferentIncomeShipmentDepartment"
                            checked={process.hasDifferentIncomeShipmentDepartment}
                            label="Oddział przyjęcia jest inny niż oddział, który obsługuje serwis."
                            onChange={onChangeHandler}
                        />
                    </Form.Group>

                    {process.hasDifferentIncomeShipmentDepartment && (
                        <div className="mb-3 p-2 pb-0 bg-light border">

                            <Form.Group className="mb-3">
                                <Form.Label>Oddział przyjęcia</Form.Label>
                                <DepartmentSelect
                                    onChange={onIncomeShipmentDepartmentChangeHandler}
                                    value={process?.incomeShipment?.department}
                                    isInvalid={!!formErrors?.incomeShipment?.department}
                                />
                                <FormError errors={formErrors?.incomeShipment?.department} />
                            </Form.Group>
                        </div>
                    )}
                </>
            )}

            {(   process?.incomeShipment?.shipmentMethod?.key === ShipmentMethodKeyEnum.courier
                || process?.incomeShipment?.shipmentMethod?.key === ShipmentMethodKeyEnum.mobile
            ) && (
                <>
                    <Form.Group className="mb-3" controlId="hasDifferentIncomeShipmentAddress">
                        <Form.Check
                            type="switch"
                            name="hasDifferentIncomeShipmentAddress"
                            checked={process.hasDifferentIncomeShipmentAddress}
                            label="Adres odbioru jest inny niż adres klienta."
                            onChange={onChangeHandler}
                        />
                    </Form.Group>

                    {process.hasDifferentIncomeShipmentAddress && (
                        <div className="mb-3 p-2 pb-0 bg-light border">
                            <div className="d-flex gap-3">
                                <legend className="fs-3">Adres odbioru od klienta</legend>
                                <div>
                                    <Button onClick={copyAddressFromCustomer}
                                            variant={"outline-azure"} size={"sm"}
                                    >skopiuj z danych klienta</Button>
                                </div>
                            </div>
                            <AddressForm
                                address={process?.incomeShipment?.address}
                                formErrors={formErrors?.incomeShipment?.address}
                                setAddress={setIncomeShipmentAddress}
                            />
                        </div>
                    )}
                </>
            )}
        </>
    );
}

export default ProcessFormIncomeShipment;